import { Checkbox, Input } from '@fluentui/react-components';
import { Checkmark20Regular, Delete20Regular, Dismiss20Regular, Edit20Regular } from '@fluentui/react-icons';
import { Button } from '../../../../Button';
import { Flex } from '../../../../Flex';
import { Text } from '../../../../Text';
import { generateDateFromTime, TimeSlot } from '@mnspro-cloud/services';
import cn from 'classnames';
import moment from 'moment-timezone';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Size } from '../Enums/Size';
import { TitleWeight } from '../Enums/TitleWeight';
import TimeRangeSection from '../TimeRangeSection/TimeRangeSection';
import style from './TimeMapping.module.css';
import { TimeRangeEditor } from './TimeRangeEditor';

export interface TimeMappingProps {
  name: string;
  start: string;
  end: string;
  isBreak?: boolean;
  displayMode?: boolean;
  deleteClicked?: () => void;
  cancelButtonClicked?: () => void;
  updateItem?: (updatedValue: TimeSlot) => void;
  /** Whether the component should start in editing mode (for new item creation) */
  startInEditMode?: boolean;
  /** Whether isBreak can be changed (only for items not yet saved to API) */
  canChangeBreakStatus?: boolean;
  validityChanged?: (isValid: boolean) => void;
}

export const TimeMapping: FC<TimeMappingProps> = props => {
  const {
    name,
    start,
    end,
    isBreak,
    startInEditMode,
    canChangeBreakStatus,
    displayMode,
    deleteClicked,
    updateItem,
    cancelButtonClicked,
    validityChanged,
  } = props;
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState<boolean>(startInEditMode ?? false);
  const [displayModel, setDisplayModel] = useState<TimeSlot>({
    start,
    end,
    name,
    isBreak: isBreak ?? false,
  });

  const [updateModel, setUpdateModel] = useState<TimeSlot>({
    start,
    end,
    name,
    isBreak: isBreak ?? false,
  });
  const [titleWarnings, setTitleWarnings] = useState<string[]>([]);
  const [timeWarnings, setTimeWarnings] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const titleRequiredMessage = t('title_required', { defaultValue: 'Bitte einen Titel eintragen!' });
  const endBeforeStartError = t('endtime_before_starttime', { defaultValue: 'Die Schlusszeit liegt vor der Startzeit!' });

  const validateForm = () => {
    const titleErrors: string[] = [];
    const timeErrors: string[] = [];
    if (updateModel.name.length < 1) {
      titleErrors.push(titleRequiredMessage);
    }
    if (
      moment(generateDateFromTime(updateModel.end)).isBefore(
        generateDateFromTime(updateModel.start),
      )
    ) {
      timeErrors.push(endBeforeStartError);
    }
    setTimeWarnings(timeErrors);
    setTitleWarnings(titleErrors);
    const formIsValid = titleErrors.length < 1 && timeErrors.length < 1;
    setIsFormValid(formIsValid);
    validityChanged?.(formIsValid);
  };

  useEffect(() => {
    validateForm();
  }, [updateModel]);

  const handleUpdate = () => {
    setDisplayModel(updateModel);
    setIsEditing(false);
    updateItem?.(updateModel);
  };
  const cancelUpdate = () => {
    setUpdateModel(displayModel);
    setIsEditing(false);
    cancelButtonClicked?.();
  };

  const timeSection = isEditing ? (
    <TimeRangeEditor
      from={updateModel.start}
      to={updateModel.end}
      fromChanged={start => setUpdateModel({ ...updateModel, start })}
      toChanged={end => setUpdateModel({ ...updateModel, end })}
      errorMessages={timeWarnings}
    />
  ) : (
    <TimeRangeSection
      from={displayModel.start}
      to={displayModel.end}
      textWeight={TitleWeight.Bold}
      textSize={Size.Large}
    />
  );

  const buttonSection = displayMode ? null : isEditing ? (
    <Flex gap="0.25rem">
      <Button
        appearance="transparent"
        size="small"
        icon={<Checkmark20Regular />}
        onClick={() => handleUpdate()}
        disabled={!isFormValid}
        data-testid="mappingUpdateButton"
        aria-label={t('settings:classbook:timemapping:confirm_a11y', { defaultValue: 'Schulstunde speichern' })}
      />
      <Button
        appearance="transparent"
        size="small"
        icon={<Dismiss20Regular />}
        onClick={() => cancelUpdate()}
        data-testid="mappingCancelButton"
        aria-label={t('settings:classbook:timemapping:cancel_a11y', { defaultValue: 'Abbrechen' })}
      />
    </Flex>
  ) : (
    <Flex gap="0.25rem">
      <Button
        appearance="transparent"
        size="small"
        icon={<Edit20Regular />}
        onClick={() => setIsEditing(true)}
        data-testid="mappingEditButton"
        aria-label={t('settings:classbook:timemapping:edit_a11y', { defaultValue: 'Schulstunde bearbeiten' })}
      />
      <Button
        appearance="transparent"
        size="small"
        icon={<Delete20Regular />}
        onClick={deleteClicked}
        data-testid="mappingDeleteButton"
        aria-label={t('settings:classbook:timemapping:delete_a11y', { defaultValue: 'Schulstunde löschen' })}
      />
    </Flex>
  );

  const titleSection = isEditing ? (
    <Flex fill direction="column" align="stretch">
      <Input
        appearance="filled-darker"
        data-testid="mappingTitleInput"
        type="text"
        defaultValue={updateModel.name}
        onChange={(_, data) =>
          setUpdateModel({ ...updateModel, name: data?.value ?? '' })
        }
      />
      {titleWarnings.map((warning, index) => (
        <Text color="danger" key={index}>{warning}</Text>
      ))}
    </Flex>
  ) : (
    <Text weight="bold" data-testid="mappingTitleText">
      {displayModel.name}
    </Text>
  );
  // Checkbox editable only for items not yet saved to API (canChangeBreakStatus=true)
  // For existing slots, isBreak cannot be changed via API
  const breakCheckboxSection = isEditing ? (
    <Checkbox
      checked={updateModel.isBreak}
      disabled={!canChangeBreakStatus}
      onChange={(_, data) =>
        setUpdateModel({ ...updateModel, isBreak: data.checked === true })
      }
      label={t('pc:settings:time_mapping:is_break', { defaultValue: 'Ist eine Pause' })}
    />
  ) : null;

  return (
    <fieldset
      className={cn(style.timeMapping, {
        [style.break]: updateModel.isBreak,
      })}
    >
      <legend>{titleSection}</legend>
      <Flex align="center" gap="0.75rem">
        <Flex fill>
          {timeSection}
          {breakCheckboxSection}
        </Flex>
        {buttonSection}
      </Flex>
    </fieldset>
  );
};
